import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Select from 'react-select'
import { Button, Col, Form, FormFeedback, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap'
import * as Yup from 'yup'
import { formatAgentOptions } from '../../../api/agents/formatters'
import agents from '../../../api/agents/hooks'
import { ReassignAgentPayload } from '../../../api/agents/types'
import { MODAL_TYPES } from '../../../common/constants'
import { hideModal } from '../../../store/modal'
import { resetTableCheckbox } from '../../../store/table'
import { getUrlBase } from '../../../utils/general'
import styles from './ChangeAgentModal.module.css'

export interface ChangeAgentModalData {
    id: number
    internalAppId: number | null
    city?: string | null
    agentFirstName?: string | null
    agentLastName?: string | null
    contactPerson?: { firstName: string; lastName: string }
}

interface ChangeAgentModalProps {
    isOpen: boolean
    changeAgentModal?: {
        modalData: ChangeAgentModalData[]
    }
}

const ChangeAgentModal = (props: ChangeAgentModalProps) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { pathname } = useLocation()
    const dataKey = pathname.slice(1) as ReassignAgentPayload['dataKey']

    const { isOpen, changeAgentModal } = props

    const handleClose = () => {
        dispatch(hideModal({ modalType: MODAL_TYPES.CHANGE_AGENT_MODAL }))
        dispatch(resetTableCheckbox(true))
    }

    const { data: agentsData } = agents.GetAgents.useQuery()
    const { mutate: reassignAgent, isPending } = agents.ReassignAgent.useMutation()

    const agentOptions = formatAgentOptions(agentsData)
    const modalData = changeAgentModal?.modalData ?? []

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: { agentId: '' },
        validationSchema: Yup.object({
            agentId: Yup.string().required(t('app.common.selectAgent')),
        }),
        onSubmit: (values) => {
            const urlBase = getUrlBase(dataKey) as ReassignAgentPayload['urlBase']

            reassignAgent(
                {
                    agentId: values.agentId,
                    ids: modalData.map((row) => row.id),
                    dataKey,
                    urlBase,
                },
                { onSuccess: handleClose }
            )
        },
    })

    const modalTitleLabels: Record<string, string> = {
        projects: t('app.common.project.label'),
        properties: t('modal.changeAgent.property'),
        contacts: t('modal.changeAgent.contact'),
    }

    const agentError = formik.touched.agentId && formik.errors.agentId

    return (
        <Modal isOpen={isOpen} fullscreen="md" centered>
            <ModalHeader toggle={handleClose}>
                <p className="m-0">{t('app.common.switchAgent')}</p>
            </ModalHeader>
            <ModalBody>
                <div className={`${styles.displayBox} py-4`}>
                    <Row>
                        <Col className="fw-bold">{modalTitleLabels[dataKey]}</Col>
                        <Col className="fw-bold">{t('app.common.agent')}</Col>
                    </Row>
                    {modalData.map((row, i) => {
                        const additionalData = dataKey === 'contacts' ? `${row?.contactPerson?.firstName} ${row?.contactPerson?.lastName}` : row.city

                        return (
                            <Row className={`${styles.displayData} my-2`} key={i}>
                                <Col>{`${row.internalAppId}${additionalData ? `, ${additionalData}` : ''}`}</Col>
                                <Col>
                                    {row.agentFirstName} {row.agentLastName}
                                </Col>
                            </Row>
                        )
                    })}
                </div>
                <Form onSubmit={formik.handleSubmit} className="mt-4 d-flex flex-column">
                    <Col sm={12} className="mb-3">
                        <Label for="agentId">{t('app.common.selectAgent')}</Label>
                        <Select
                            inputId="agentId"
                            options={agentOptions}
                            onChange={(e) => formik.setFieldValue('agentId', e ? e.value : '')}
                            onBlur={() => formik.setFieldTouched('agentId')}
                            value={agentOptions.find((o) => o.value === formik.values.agentId) ?? null}
                            placeholder=""
                            noOptionsMessage={() => t('form.common.select.noData')}
                            className={agentError ? 'is-invalid' : ''}
                            isClearable
                        />
                        {agentError && <FormFeedback type="invalid">{formik.errors.agentId}</FormFeedback>}
                    </Col>
                    <Button className="btn-label align-self-end" type="submit" color="success" disabled={isPending}>
                        <i className="mdi mdi-check label-icon align-middle fs-16 me-2"></i> {t('button.save')}
                    </Button>
                </Form>
            </ModalBody>
        </Modal>
    )
}

export default ChangeAgentModal
